import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Circle, FileText } from 'lucide-react';
import axios from 'axios';
import { PdfViewer } from '../../components/PdfViewer';
import { PublicPageCloseButton } from '../../components/PublicPageCloseButton';
import { usePublicPackageSend, usePublicSign } from './hooks';
import type { PublicDocumentInstance } from './types';

export function PublicSignPage() {
  const { nonce = '' } = useParams();
  const { data, isLoading, error, refetch } = usePublicPackageSend(nonce);
  const sign = usePublicSign(nonce);

  const [activeDoc, setActiveDoc] = useState<PublicDocumentInstance | null>(null);
  const [typedName, setTypedName] = useState('');
  const [email, setEmail] = useState('');
  const [acknowledge, setAcknowledge] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  const onSign = async () => {
    if (!activeDoc) return;
    setSignError(null);
    try {
      await sign.mutateAsync({
        documentInstanceId: activeDoc.id,
        typedName: typedName.trim(),
        signerEmail: email.trim()
      });
      setActiveDoc(null);
      setAcknowledge(false);
      setTypedName('');
      await refetch();
    } catch (err) {
      setSignError(readMessage(err) ?? 'Signing failed — try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="flex items-center justify-between">
          <div className="text-sky-700 font-bold text-lg">ReceiptRaptor</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-slate-500">Secure signing</div>
            <PublicPageCloseButton />
          </div>
        </header>

        {isLoading && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
            Loading documents…
          </div>
        )}

        {error && <ErrorCard error={error} />}

        {data && !activeDoc && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div>
              <p className="text-xs uppercase text-slate-500 tracking-wide">From</p>
              <h1 className="text-xl font-semibold text-slate-800">{data.businessName}</h1>
              <p className="text-sm text-slate-500">Addressed to {data.customerName}</p>
            </div>
            <p className="text-sm text-slate-600">
              {data.items.length} document{data.items.length === 1 ? '' : 's'} for your review. Sign in the order shown.
            </p>
            <ul className="space-y-2">
              {data.items.map((item) => (
                <li key={item.id} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {item.status === 'Signed' ? (
                      <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                    ) : !item.isRequired ? (
                      <FileText size={18} className="text-slate-400 shrink-0" />
                    ) : (
                      <Circle size={18} className="text-slate-400 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{item.templateName}</div>
                      <div className="text-xs text-slate-500">
                        {humanizeKind(item.kind)}
                        {item.effectiveFromUtc
                          ? ` • Effective ${new Date(item.effectiveFromUtc).toLocaleDateString()} – ${new Date(item.effectiveUntilUtc).toLocaleDateString()}`
                          : ` • Valid until ${new Date(item.effectiveUntilUtc).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                  {!item.isRequired ? (
                    <span className="text-xs text-slate-500">No signature needed</span>
                  ) : item.status === 'Sent' && data.nextToSignId === item.id ? (
                    <button
                      type="button"
                      onClick={() => setActiveDoc(item)}
                      className="bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 text-sm inline-flex items-center gap-2"
                    >
                      <FileText size={16} /> Review & sign
                    </button>
                  ) : item.status === 'Sent' ? (
                    <span className="text-xs text-slate-500">Waiting for previous</span>
                  ) : (
                    <span className="text-xs text-green-700">Signed</span>
                  )}
                </li>
              ))}
            </ul>
            {data.items.some((i) => i.isRequired) &&
              data.items.filter((i) => i.isRequired).every((i) => i.status !== 'Sent') && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-3">
                  All documents signed. You can close this page.
                </p>
              )}
          </section>
        )}

        {data && activeDoc && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">{activeDoc.templateName}</h2>
                <p className="text-xs text-slate-500">{humanizeKind(activeDoc.kind)}</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveDoc(null)}
                className="text-slate-600 hover:text-sky-600 text-sm"
              >
                Back
              </button>
            </div>
            <PdfViewer url={activeDoc.pdfUrl} />
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1">Your full name (typed signature)</span>
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1">Your email</span>
                <input
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                />
              </label>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={acknowledge}
                onChange={(e) => setAcknowledge(e.target.checked)}
                className="mt-1"
              />
              <span>
                I acknowledge this typed signature carries the same intent as my handwritten
                signature for this document.
              </span>
            </label>
            {signError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{signError}</p>}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setActiveDoc(null)}
                className="text-slate-600 hover:text-sky-600 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSign}
                disabled={!typedName.trim() || !email.trim() || !acknowledge || sign.isPending}
                className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
              >
                {sign.isPending ? 'Signing…' : 'Sign'}
              </button>
            </div>
          </section>
        )}

        <footer className="text-center text-xs text-slate-400 pt-4">
          Signed sessions are valid for 72 hours. IP and user-agent are recorded on each signature.
        </footer>
      </div>
    </div>
  );
}

function humanizeKind(kind: string): string {
  return {
    PowerOfAttorney: 'Power of attorney',
    VetAuthorization: 'Vet authorization',
    IncapacitationDirective: 'Incapacitation directive',
    Other: 'Other'
  }[kind] ?? kind;
}

function readMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as { message?: string } | undefined;
  return data?.message ?? null;
}

function ErrorCard({ error }: { error: unknown }) {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  let title = 'Something went wrong';
  let body = 'Please ask your sitter to resend the documents.';
  if (status === 404) { title = 'Link not found'; body = 'This signing link is invalid or has been revoked.'; }
  else if (status === 410) { title = 'Link expired'; body = 'This signing link has expired or was already used.'; }
  else if (status === 429) { title = 'Too many requests'; body = 'Please wait a minute and try again.'; }
  return (
    <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
      <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      <p className="text-sm text-slate-600 mt-2">{body}</p>
    </div>
  );
}
